import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Dices, BatteryCharging, Radar } from "lucide-react";
import { industries } from "../../data/mock";
import { Reveal } from "../../components/animations/Reveal";
import { cn } from "@/lib/utils";

const industryIcons = { Dices, BatteryCharging, Radar };

const accentClasses = {
  igaming: {
    text: "text-[hsl(var(--igaming))]",
    bg: "bg-[hsl(var(--igaming))]/10",
    border: "hover:border-[hsl(var(--igaming))]/50",
    bar: "from-[hsl(var(--igaming))] to-[hsl(var(--igaming-2))]",
  },
  battery: {
    text: "text-[hsl(var(--battery))]",
    bg: "bg-[hsl(var(--battery))]/10",
    border: "hover:border-[hsl(var(--battery))]/50",
    bar: "from-[hsl(var(--battery))] to-[hsl(var(--battery-2))]",
  },
  automotive: {
    text: "text-[hsl(var(--automotive))]",
    bg: "bg-[hsl(var(--automotive))]/10",
    border: "hover:border-[hsl(var(--automotive))]/50",
    bar: "from-[hsl(var(--automotive))] to-[hsl(var(--automotive-2))]",
  },
};

export default function IndustriesIndex() {
  return (
    <div className="bg-background">
      <section className="relative overflow-hidden py-20 lg:py-28 border-b border-border">
        <div className="absolute inset-0 graph-bg" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-primary text-sm font-mono uppercase tracking-wider mb-4">Industries</p>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground leading-tight mb-6">
              Three Industries. One Engineering Discipline.
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              SCADA telemetry, ADAS/AV sensor data, and iGaming transaction data each carry a
              different compliance regime and threat model. We design for the one you're in.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {industries.map((ind, i) => {
            const Icon = industryIcons[ind.icon] || Dices;
            const accent = accentClasses[ind.accent];
            return (
              <Reveal key={ind.slug} delay={i * 120}>
                <Link
                  to={`/industries/${ind.slug}`}
                  className={cn(
                    "group flex flex-col h-full rounded-2xl bg-card border border-border overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-black/20",
                    accent.border
                  )}
                >
                  <div className={cn("h-1 w-full bg-gradient-to-r", accent.bar)} />
                  <div className="p-8 flex flex-col flex-1">
                    <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center mb-6", accent.bg)}>
                      <Icon className={cn("w-6 h-6", accent.text)} />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">{ind.fullName}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">{ind.dataChallenge}</p>
                    <div className="mt-auto flex items-center text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                      Explore {ind.name}
                      <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}
